/**
 * Helpers for Bin Controller activity history + per-bin inventory charts.
 * Mirrors {@link WideBinSetStatusCard} chart data rules where noted.
 */

import { FaultCodes } from '@feedflo/fault-codes';

/** Match server-side past trim in bin set level prediction job (see Compute binset_feed_calculator.serialization). */
export const BIN_ACTIVITY_HISTORY_FALLBACK_DAYS = 28;

/** User-facing sources for slide events (second axis: how the slide was initiated). */
export const SLIDE_EVENT_SOURCE_MANUAL = 'Manual';
export const SLIDE_EVENT_SOURCE_REMOTE = 'Remote';
export const SLIDE_EVENT_SOURCE_AUTO = 'Auto';
export const SLIDE_EVENT_SOURCE_FEEDFLO_STAFF = 'FeedFlo Staff';

/**
 * Marker fill by **operator** bucket: only two colors on the chart.
 * - **Auto** — system-initiated slide.
 * - **Manual** — Manual, Remote, FeedFlo Staff, and unknown sources share this violet; exact source is in the tooltip.
 *   Violet (over a primary blue) keeps Manual visually distinct from chart axes/grid blues and from Auto green.
 */
export const SLIDE_EVENT_SOURCE_MARKER_COLORS = {
  [SLIDE_EVENT_SOURCE_MANUAL]: '#6080fd',
  [SLIDE_EVENT_SOURCE_AUTO]: '#16a34a',
};

export const SLIDE_EVENT_INTERACTION_OPEN = 'Open';
export const SLIDE_EVENT_INTERACTION_CLOSE = 'Close';
export const SLIDE_EVENT_INTERACTION_VIBRATION = 'Vibration';

/**
 * @param {string | undefined} source raw marker `source`
 * @returns {string} hex fill for the marker disk
 */
export function slideEventMarkerFillForSource(source) {
  if (source === SLIDE_EVENT_SOURCE_AUTO) {
    return SLIDE_EVENT_SOURCE_MARKER_COLORS[SLIDE_EVENT_SOURCE_AUTO];
  }
  return SLIDE_EVENT_SOURCE_MARKER_COLORS[SLIDE_EVENT_SOURCE_MANUAL];
}

const SWITCH_CODES = new Set([
  FaultCodes.BIN_CONTROLLER_SLIDE_OPEN,
  FaultCodes.BIN_CONTROLLER_MANUAL_SLIDE_SWITCH,
  FaultCodes.BIN_CONTROLLER_LOCAL_NETWORK_SLIDE_SWITCH,
  FaultCodes.BIN_CONTROLLER_AUTOMATIC_SLIDE_SWITCH,
  FaultCodes.BIN_CONTROLLER_INTERNET_SLIDE_SWITCH,
  FaultCodes.BIN_CONTROLLER_UNKNOWN_SLIDE_SWITCH,
]);

const VIBRATE_CODES = new Set([
  FaultCodes.BIN_CONTROLLER_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_MANUAL_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_LOCAL_NETWORK_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_AUTOMATIC_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_INTERNET_VIBRATE_ONLY,
  FaultCodes.BIN_CONTROLLER_UNKNOWN_VIBRATE_ONLY,
]);

/** Either family — used to decide whether a raw fault belongs in the slide-activity timeline at all. */
const SLIDE_ACTIVITY_CODES = new Set([...SWITCH_CODES, ...VIBRATE_CODES]);

/**
 * Single canonical classification for a merged step's codes. Vibration wins if both families are somehow
 * present (shouldn't happen in practice — the two families are disjoint fault codes), so every other piece
 * of logic that needs to tell switch from vibration goes through this one function.
 *
 * @param {number[]} codes
 * @returns {'switch' | 'vibration'}
 */
function classifyMergedStep(codes) {
  return codes.some((c) => VIBRATE_CODES.has(c)) ? 'vibration' : 'switch';
}

/**
 * @param {Record<string, unknown>} map
 * @returns {boolean}
 */
export function hasValidPerBinLevels(map) {
  if (map == null || typeof map !== 'object' || Array.isArray(map)) return false;
  return Object.values(map).some((v) => typeof v === 'number' && Number.isFinite(v));
}

/**
 * @param {Array<{ t: number, v: number }>} seriesArrays
 * @returns {{ domainStart: number, domainEnd: number } | null}
 */
export function getDomainFromTimeSeriesArrays(seriesArrays) {
  const all = seriesArrays.flatMap((s) => (Array.isArray(s) ? s : []));
  if (all.length === 0) return null;
  const ts = all.map((p) => p.t).filter((t) => typeof t === 'number' && Number.isFinite(t));
  if (ts.length === 0) return null;
  return { domainStart: Math.min(...ts), domainEnd: Math.max(...ts) };
}

/**
 * Build per-bin chart props ordered by bin controller index (0, 1, …).
 *
 * @param {Array<{ id?: string, name?: string, capacityInGrams?: number }>} binsFromSet
 * @param {Array<{ index: number, bin?: { id?: string, name?: string } }>} binControllerBins
 * @param {{
 *   pastByBinId?: Record<string, Array<{ t: number, v: number }>>,
 *   deliveredByBinId?: Record<string, Array<{ t: number, v: number }>>,
 *   scheduledByBinId?: Record<string, Array<{ t: number, v: number }>>,
 *   recommendedByBinId?: Record<string, Array<{ t: number, v: number }>>,
 * } | null} perBinChartData
 * @returns {Array<{
 *   binId: string,
 *   binIndex: number,
 *   binName: string,
 *   step_bin_level_history: Array<{ t: number, v: number }>,
 *   projection_past_deliveries: Array<{ t: number, v: number }>,
 *   projection_with_scheduled: Array<{ t: number, v: number }>,
 *   projection_with_recommended: Array<{ t: number, v: number }>,
 *   capacityGrams: number,
 * }>}
 */
export function buildPerBinChartsForController(binsFromSet, binControllerBins, perBinChartData) {
  if (!perBinChartData) return [];

  const pastByBinId = perBinChartData.pastByBinId || {};
  const deliveredByBinId = perBinChartData.deliveredByBinId || {};
  const scheduledByBinId = perBinChartData.scheduledByBinId || {};
  const recommendedByBinId = perBinChartData.recommendedByBinId || {};

  const capById = {};
  for (const b of binsFromSet || []) {
    if (b?.id) capById[b.id] = b.capacityInGrams || 0;
  }

  const sorted = [...(binControllerBins || [])].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const out = [];

  for (const bcb of sorted) {
    const binId = bcb?.bin?.id;
    if (!binId) continue;

    const past = pastByBinId[binId];
    const delivered = deliveredByBinId[binId];
    const scheduled = scheduledByBinId[binId];
    const recommended = recommendedByBinId[binId];

    const hasAny =
      past?.length > 0 ||
      delivered?.length > 0 ||
      scheduled?.length > 0 ||
      recommended?.length > 0;
    if (!hasAny) continue;

    out.push({
      binId,
      binIndex: bcb.index,
      binName: bcb.bin?.name || binsFromSet?.find((b) => b.id === binId)?.name || '?',
      step_bin_level_history: past || [],
      projection_past_deliveries: delivered || [],
      projection_with_scheduled: scheduled || [],
      projection_with_recommended: recommended || [],
      capacityGrams: capById[binId] || 0,
    });
  }

  return out;
}

/**
 * One row per `bin_controller_bin` (by index), including bins with no series so markers still render.
 *
 * @param {Array<{ id?: string, name?: string, capacityInGrams?: number }>} binsFromSet
 * @param {Array<{ index: number, bin?: { id?: string, name?: string } }>} binControllerBins
 * @param {Parameters<typeof buildPerBinChartsForController>[2]} perBinChartData
 */
export function buildPerBinChartRowsForActivityHistory(binsFromSet, binControllerBins, perBinChartData) {
  const withData = buildPerBinChartsForController(binsFromSet, binControllerBins, perBinChartData);
  const byIndex = new Map(withData.map((c) => [c.binIndex, c]));
  const capById = {};
  for (const b of binsFromSet || []) {
    if (b?.id) capById[b.id] = b.capacityInGrams || 0;
  }

  const sorted = [...(binControllerBins || [])].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const rows = [];
  for (const bcb of sorted) {
    const binId = bcb?.bin?.id;
    if (!binId) continue;
    const hit = byIndex.get(bcb.index);
    if (hit) {
      rows.push(hit);
      continue;
    }
    rows.push({
      binId,
      binIndex: bcb.index,
      binName: bcb.bin?.name || binsFromSet?.find((b) => b.id === binId)?.name || '?',
      step_bin_level_history: [],
      projection_past_deliveries: [],
      projection_with_scheduled: [],
      projection_with_recommended: [],
      capacityGrams: capById[binId] || 0,
    });
  }
  return rows;
}

/**
 * @param {Array<ReturnType<typeof buildPerBinChartsForController>[number]>} perBinCharts
 * @returns {{ domainStart: number, domainEnd: number } | null}
 */
export function getDomainFromPerBinCharts(perBinCharts) {
  const arrays = [];
  for (const c of perBinCharts) {
    arrays.push(
      c.step_bin_level_history,
      c.projection_past_deliveries,
      c.projection_with_scheduled,
      c.projection_with_recommended,
    );
  }
  return getDomainFromTimeSeriesArrays(arrays);
}

/**
 * Sort slide faults by time, then merge rows that share the same `started_at` and `binIndex` so co-emitted
 * faults (e.g. slide-open + device switch) become one timeline step. Source uses all `code`s in the merge.
 *
 * @param {Array<{ id: string, code: number, started_at: number, details?: string }>} slideFaultsSorted slide faults sorted by `started_at`
 * @returns {Array<{ id: string, started_at: number, details?: string, codes: number[], faultIds: string[] }>}
 */
export function mergeSlideFaultsByStartedAtAndBin(slideFaultsSorted) {
  /** @type {Map<string, { id: string, started_at: number, details?: string, codes: number[], faultIds: string[] }>} */
  const byKey = new Map();
  for (const f of slideFaultsSorted) {
    const idx = parseBinIndexFromFaultDetails(f.details);
    if (idx == null) continue;
    const t = Number(f.started_at);
    const key = `${t}:${idx}`;
    const hit = byKey.get(key);
    if (!hit) {
      byKey.set(key, {
        id: f.id,
        started_at: t,
        details: f.details,
        codes: [f.code],
        faultIds: [f.id],
      });
    } else {
      if (!hit.codes.includes(f.code)) hit.codes.push(f.code);
      hit.faultIds.push(f.id);
    }
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.started_at !== b.started_at) return a.started_at - b.started_at;
    const ia = parseBinIndexFromFaultDetails(a.details) ?? -1;
    const ib = parseBinIndexFromFaultDetails(b.details) ?? -1;
    return ia - ib;
  });
}

/**
 * @param {Array<{ id: string, code: number, started_at: number, details?: string }>} faultsAsc
 * @returns {ReturnType<typeof mergeSlideFaultsByStartedAtAndBin>}
 */
function normalizeMergedSlideFaultTimeline(faultsAsc) {
  const slide = faultsAsc.filter((f) => SLIDE_ACTIVITY_CODES.has(f.code));
  slide.sort((a, b) => {
    const ta = Number(a.started_at);
    const tb = Number(b.started_at);
    if (ta !== tb) return ta - tb;
    const ia = parseBinIndexFromFaultDetails(a.details) ?? -1;
    const ib = parseBinIndexFromFaultDetails(b.details) ?? -1;
    return ia - ib;
  });
  return mergeSlideFaultsByStartedAtAndBin(slide);
}

/** First matching bucket wins when several slide/vibrate reason codes are merged at one instant. */
const SLIDE_SOURCE_BY_PRIORITY = [
  {
    source: SLIDE_EVENT_SOURCE_MANUAL,
    codes: [FaultCodes.BIN_CONTROLLER_MANUAL_SLIDE_SWITCH, FaultCodes.BIN_CONTROLLER_MANUAL_VIBRATE_ONLY],
  },
  {
    source: SLIDE_EVENT_SOURCE_AUTO,
    codes: [FaultCodes.BIN_CONTROLLER_AUTOMATIC_SLIDE_SWITCH, FaultCodes.BIN_CONTROLLER_AUTOMATIC_VIBRATE_ONLY],
  },
  {
    source: SLIDE_EVENT_SOURCE_REMOTE,
    codes: [
      FaultCodes.BIN_CONTROLLER_LOCAL_NETWORK_SLIDE_SWITCH,
      FaultCodes.BIN_CONTROLLER_INTERNET_SLIDE_SWITCH,
      FaultCodes.BIN_CONTROLLER_LOCAL_NETWORK_VIBRATE_ONLY,
      FaultCodes.BIN_CONTROLLER_INTERNET_VIBRATE_ONLY,
    ],
  },
  {
    source: SLIDE_EVENT_SOURCE_FEEDFLO_STAFF,
    codes: [FaultCodes.BIN_CONTROLLER_SLIDE_OPEN, FaultCodes.BIN_CONTROLLER_VIBRATE_ONLY],
  },
];

/**
 * Source for one merged instant: walk {@link SLIDE_SOURCE_BY_PRIORITY} and return the first label whose
 * codes appear in the merged set (Manual → Auto → Remote → FeedFlo Staff).
 *
 * @param {number[]} codes
 * @returns {typeof SLIDE_EVENT_SOURCE_MANUAL | typeof SLIDE_EVENT_SOURCE_REMOTE | typeof SLIDE_EVENT_SOURCE_AUTO | typeof SLIDE_EVENT_SOURCE_FEEDFLO_STAFF | '—'}
 */
export function sourceForMergedSlideCodes(codes) {
  const unique = new Set(codes);
  for (const { source, codes: matchCodes } of SLIDE_SOURCE_BY_PRIORITY) {
    if (matchCodes.some((c) => unique.has(c))) return source;
  }
  return '—';
}

/**
 * The one "current active bin" state machine, shared by every consumer that needs to know which bin was
 * active when: {@link buildActiveBinIntervalsByBinIndex}, {@link getActiveBinIndexAtEnd}, and the Open/Close
 * half of {@link buildSlideEventMarkersByBinIndex}. Walking this once (instead of re-deriving "current active
 * bin" separately in each consumer) guarantees they can never disagree.
 *
 * Vibration steps are skipped — they never change which bin is active.
 *
 * @param {Array<{ id: string, code: number, started_at: number, details?: string }>} faultsAsc
 * @returns {Array<{ t: number, idx: number, prevIdx: number | null, source: string, faultIds: string[] }>}
 */
function buildSwitchEvents(faultsAsc) {
  const events = [];
  let currentActiveBin = null;

  for (const step of normalizeMergedSlideFaultTimeline(faultsAsc)) {
    if (classifyMergedStep(step.codes) !== 'switch') continue;
    const idx = parseBinIndexFromFaultDetails(step.details);
    if (idx == null) continue;

    events.push({
      t: Number(step.started_at),
      idx,
      prevIdx: currentActiveBin,
      source: sourceForMergedSlideCodes(step.codes),
      faultIds: step.faultIds,
    });
    currentActiveBin = idx;
  }

  return events;
}

/**
 * @param {string | null | undefined} detailsBase64
 * @returns {number | null}
 */
export function parseBinIndexFromFaultDetails(detailsBase64) {
  if (!detailsBase64) return null;
  try {
    const obj = JSON.parse(atob(detailsBase64));
    const idx = obj?.binIndex;
    return typeof idx === 'number' && Number.isFinite(idx) ? idx : null;
  } catch {
    return null;
  }
}

/**
 * @param {Array<{ id: string, code: number, started_at: number, ended_at?: number | null, details?: string }>} faults
 * @param {number} domainStart
 * @param {number} domainEnd
 * @param {number} nowUnix
 * @returns {Array<{ tStart: number, tEnd: number, color: string, opacity: number, label: string }>}
 */
export function buildLockdownBackgroundBands(faults, domainStart, domainEnd, nowUnix) {
  const bands = [];
  for (const f of faults) {
    if (f.code !== FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN && f.code !== FaultCodes.BIN_CONTROLLER_SOFT_LOCK_DOWN) {
      continue;
    }
    const isHard = f.code === FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN;
    const started = Number(f.started_at);
    const endedRaw = f.ended_at != null ? Number(f.ended_at) : nowUnix;
    const tStart = Math.max(domainStart, started);
    const tEnd = Math.min(domainEnd, endedRaw);
    if (tEnd <= tStart) continue;
    bands.push({
      tStart,
      tEnd,
      color: isHard ? '#ef4444' : '#eab308',
      opacity: isHard ? 0.22 : 0.2,
      label: isHard ? 'Hard lockdown' : 'Soft lockdown',
    });
  }
  return bands;
}

/** Fill for {@link buildInactiveBinBackgroundBandsByBinIndex}: muted grey for "other bin selected" periods. */
const INACTIVE_BIN_BAND_COLOR = '#94a3b8';

/**
 * Merge overlapping/adjacent intervals.
 *
 * @param {Array<{ tStart: number, tEnd: number }>} intervals
 * @returns {Array<{ tStart: number, tEnd: number }>}
 */
function unionIntervals(intervals) {
  const cleaned = intervals
    .filter((iv) => Number.isFinite(iv.tStart) && Number.isFinite(iv.tEnd) && iv.tEnd > iv.tStart)
    .map((iv) => ({ tStart: iv.tStart, tEnd: iv.tEnd }))
    .sort((a, b) => a.tStart - b.tStart);
  const merged = [];
  for (const iv of cleaned) {
    const last = merged[merged.length - 1];
    if (!last || iv.tStart > last.tEnd) {
      merged.push({ ...iv });
    } else {
      last.tEnd = Math.max(last.tEnd, iv.tEnd);
    }
  }
  return merged;
}

/**
 * `[domainStart, domainEnd]` minus the union of `intervals`.
 *
 * @param {number} domainStart
 * @param {number} domainEnd
 * @param {Array<{ tStart: number, tEnd: number }>} intervals
 * @returns {Array<{ tStart: number, tEnd: number }>}
 */
function subtractIntervalsFromDomain(domainStart, domainEnd, intervals) {
  if (!(domainEnd > domainStart)) return [];
  const merged = unionIntervals(intervals);
  const out = [];
  let cursor = domainStart;
  for (const iv of merged) {
    const s = Math.max(iv.tStart, domainStart);
    const e = Math.min(iv.tEnd, domainEnd);
    if (e <= cursor) continue;
    if (s >= domainEnd) break;
    if (s > cursor) out.push({ tStart: cursor, tEnd: s });
    cursor = Math.max(cursor, e);
  }
  if (cursor < domainEnd) out.push({ tStart: cursor, tEnd: domainEnd });
  return out;
}

/**
 * For each bin index, time ranges when that bin was the active slide target (switch events only).
 * Used as the basis for the inverted "inactive" bands.
 *
 * @param {Array<{ id: string, code: number, started_at: number, details?: string }>} faultsAsc
 * @param {number} domainStart
 * @param {number} domainEnd
 * @returns {Map<number, Array<{ tStart: number, tEnd: number }>>}
 */
function buildActiveBinIntervalsByBinIndex(faultsAsc, domainStart, domainEnd) {
  // Only transitions (bin actually changed) start a new active interval — reselecting the same bin is a no-op here.
  const transitions = buildSwitchEvents(faultsAsc).filter((e) => e.idx !== e.prevIdx);

  /** @type {Map<number, Array<{ tStart: number, tEnd: number }>>} */
  const byBin = new Map();
  transitions.forEach(({ idx, t }, k) => {
    const tEndRaw = k + 1 < transitions.length ? transitions[k + 1].t : domainEnd;
    const t0 = Math.max(domainStart, t);
    const t1 = Math.min(domainEnd, tEndRaw);
    if (t1 > t0) {
      if (!byBin.has(idx)) byBin.set(idx, []);
      byBin.get(idx).push({ tStart: t0, tEnd: t1 });
    }
  });

  return byBin;
}

/**
 * For each bin index, time ranges within `[domainStart, domainEnd]` when **another** bin was the active slide
 * target, minus any **subtracted** lockdown windows (typically lockdown bands currently shown on the chart, so
 * grey inactive shading does not overlap visible lockdown tint).
 *
 * Active intervals come from switch-family faults only (see {@link buildActiveBinIntervalsByBinIndex}):
 * each switch to a bin opens an active period until the next switch to a different bin, or `domainEnd`.
 *
 * @param {Array<{ id: string, code: number, started_at: number, details?: string }>} faultsAsc
 * @param {number} domainStart
 * @param {number} domainEnd
 * @param {Array<{ tStart: number, tEnd: number }>} [lockdownBands] lockdown time ranges to subtract from inactive
 *   shading (pass only intervals that are drawn as lockdown overlays, e.g. when those layers are visible)
 * @param {Array<number>} [binIndexes] all bin indexes to consider (bins never seen as active still get full
 *   domain shaded minus lockdowns)
 * @returns {Map<number, Array<{ tStart: number, tEnd: number, color: string, opacity: number, label: string }>>}
 */
export function buildInactiveBinBackgroundBandsByBinIndex(
  faultsAsc,
  domainStart,
  domainEnd,
  lockdownBands = [],
  binIndexes = [],
) {
  const activeByBin = buildActiveBinIntervalsByBinIndex(faultsAsc, domainStart, domainEnd);
  const lockdownIntervals = (lockdownBands || []).map((b) => ({ tStart: b.tStart, tEnd: b.tEnd }));

  const allBinIndexes = new Set([...activeByBin.keys(), ...(binIndexes || [])]);

  /** @type {Map<number, Array<{ tStart: number, tEnd: number, color: string, opacity: number, label: string }>>} */
  const byBin = new Map();
  for (const idx of allBinIndexes) {
    const activeIntervals = activeByBin.get(idx) || [];
    const subtract = [...activeIntervals, ...lockdownIntervals];
    const inactiveIntervals = subtractIntervalsFromDomain(domainStart, domainEnd, subtract);
    if (inactiveIntervals.length === 0) continue;
    byBin.set(
      idx,
      inactiveIntervals.map((iv) => ({
        tStart: iv.tStart,
        tEnd: iv.tEnd,
        color: INACTIVE_BIN_BAND_COLOR,
        opacity: 0.22,
        label: 'Another bin was selected',
      })),
    );
  }
  return byBin;
}

/**
 * Human-readable rules for bin-controller **slide activity** markers (see {@link buildSlideEventMarkersByBinIndex}).
 *
 * **Goal** — Two axes per marker: (1) **Actions**: Open / Close / Vibration. (2) **Source**: Manual, Remote, Auto,
 * FeedFlo Staff, or unknown (—). Open and Close only ever appear on the chart for the bin they apply to.
 *
 * **1. Inputs**
 * - Use faults sorted by `started_at` ascending (then `binIndex` as tiebreaker before merge).
 * - Only faults whose `code` is a switch-family or vibrate-family bin-controller code.
 * - Ignore faults whose `details` (base64 JSON) does not contain a finite numeric `binIndex` (omitted from merge).
 *
 * **2. Merge same instant, same bin**
 * - Merge fault rows that share the same `started_at` **and** `binIndex` into one **step** with all distinct `code`s
 *   collected. Co-emitted slide-open + device switch reason yield one marker with the device source.
 *
 * **3. Actions: Open / Close / Vibration (by fault-code family)**
 * - **Vibration** — Any merged step whose codes include a vibrate-family code. Drawn on that bin only; does not
 *   change which bin is active and never emits Close.
 * - **Open** — Any merged step whose codes are switch-family only. Selecting a **different** bin than the current
 *   active bin emits an implicit **Close** on the previous bin at the same instant, then Open on the new bin.
 * - Re-selecting the already-active bin (switch-family on same bin) still emits Open; no Close.
 *
 * **4. Source (Manual / Remote / Auto / FeedFlo Staff / —)**
 * - For each merged step, device reason codes win over base slide-open (2101) or vibrate-only (2109).
 * - Lone slide-open-only or vibrate-only-only steps resolve to **FeedFlo Staff**.
 *
 * **5. One marker per merged step (+ optional implicit close on the previous bin)**
 * - Each merge result produces at most one Open or Vibration marker on its bin. Switch to a different bin adds a
 *   Close marker on the previously active bin at the same time.
 *
 * @param {Array<{ id: string, code: number, started_at: number, details?: string }>} faultsAsc
 * @returns {Map<number, Array<{ t: number, kind: 'open' | 'close' | 'vibration', interactionLabel: string, source: string, markerFill: string, label: string, faultId: string }>>}
 */
export function buildSlideEventMarkersByBinIndex(faultsAsc) {
  /** @type {Map<number, Array<{ t: number, kind: 'open' | 'close' | 'vibration', interactionLabel: string, source: string, markerFill: string, label: string, faultId: string }>>} */
  const byBin = new Map();

  const interactionLabelForKind = {
    open: SLIDE_EVENT_INTERACTION_OPEN,
    close: SLIDE_EVENT_INTERACTION_CLOSE,
    vibration: SLIDE_EVENT_INTERACTION_VIBRATION,
  };

  const pushMarker = (idx, t, kind, source, faultIds) => {
    const interactionLabel = interactionLabelForKind[kind];
    if (!byBin.has(idx)) byBin.set(idx, []);
    byBin.get(idx).push({
      t,
      kind,
      interactionLabel,
      source,
      markerFill: slideEventMarkerFillForSource(source),
      label: `${interactionLabel} · ${source}`,
      faultId: faultIds.join(', '),
    });
  };

  // Vibration: drawn on its own bin, never touches which bin is "active".
  for (const step of normalizeMergedSlideFaultTimeline(faultsAsc)) {
    if (classifyMergedStep(step.codes) !== 'vibration') continue;
    const idx = parseBinIndexFromFaultDetails(step.details);
    if (idx == null) continue;
    pushMarker(idx, step.started_at, 'vibration', sourceForMergedSlideCodes(step.codes), step.faultIds);
  }

  // Open (+ implicit Close on the previously active bin), from the shared active-bin timeline.
  for (const { t, idx, prevIdx, source, faultIds } of buildSwitchEvents(faultsAsc)) {
    if (prevIdx != null && prevIdx !== idx) {
      pushMarker(prevIdx, t, 'close', source, faultIds);
    }
    pushMarker(idx, t, 'open', source, faultIds);
  }

  // Markers for each bin were appended in two passes (vibration, then switch) — restore chronological order.
  for (const markers of byBin.values()) markers.sort((a, b) => a.t - b.t);

  return byBin;
}

/**
 * Active bin at end of window: last switch-family event with started_at <= domainEnd.
 *
 * @param {Array<{ id: string, code: number, started_at: number, details?: string }>} faultsAsc
 * @param {number} domainEnd
 * @returns {number | null}
 */
export function getActiveBinIndexAtEnd(faultsAsc, domainEnd) {
  let lastSwitchBin = null;
  for (const { t, idx } of buildSwitchEvents(faultsAsc)) {
    if (t <= domainEnd) lastSwitchBin = idx;
  }
  return lastSwitchBin;
}
